import type { SavedSearch } from "@gjwtrack/domain";
import { ChevronDown, ChevronRight, Ellipsis, FileText, Pencil, Star, Tag, Trash2, X } from "lucide-react";
import { useEffect, useState } from "react";
import { client, useClient } from "../lib/client.ts";
import { compactCount } from "../lib/format.ts";
import { Menu } from "../ui/Menu.tsx";
import { SavedSearchDialog } from "./SavedSearchDialog.tsx";
import { uuid } from "@gjwtrack/client";

/** Drafts / Projects / Tags / Star / Saved Searches, as in YouTrack's issues page. */
export function IssuesSidePanel({ onQuery, currentQuery, onOpenDraft }: { onQuery: (q: string) => void; currentQuery: string; onOpenDraft: (id: number) => void }) {
  const schema = useClient((s) => s.schema);
  const me = useClient((s) => s.me);
  const saved = useClient((s) => s.savedSearches);
  const drafts = useClient((s) => s.drafts);
  const [open, setOpen] = useState<Record<string, boolean>>({ Projects: true, Tags: true, "Saved Searches": true, Drafts: true });
  const [editing, setEditing] = useState<SavedSearch | null>(null);
  useEffect(() => {
    void client.loadSavedSearches();
    void client.loadDrafts();
  }, []);
  const saveCurrent = async (): Promise<void> => {
    const name = prompt("Name for this search", currentQuery.slice(0, 40));
    if (!name) return;
    try {
      await client.http.command("createSavedSearch", { name, query: currentQuery, shared: false }, uuid());
      await client.loadSavedSearches();
    } catch (e) {
      client.pushError("saved_search", (e as Error).message);
    }
  };
  const section = (label: string, items: { label: string; q: string; icon?: React.ReactNode }[], disabled = false) => {
    const isOpen = open[label] ?? false;
    return (
      <div key={label} className="mb-1">
        <button
          className={`flex w-full items-center gap-1 px-3 py-1.5 text-[15px] ${disabled ? "text-faint" : "text-ink"}`}
          onClick={() => !disabled && setOpen((o) => ({ ...o, [label]: !isOpen }))}
          title={disabled ? "Not available yet" : undefined}
        >
          {isOpen && !disabled ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
          {label}
        </button>
        {isOpen && !disabled && (
          <div className="mb-1 pl-8">
            {items.length === 0 && <div className="py-1 text-muted">No {label.toLowerCase()}</div>}
            {items.map((it) => (
              <button key={it.label} className="flex w-full items-center gap-2 rounded px-2 py-1 text-left text-value hover:bg-sunken" onClick={() => onQuery(it.q)}>
                {it.icon}
                <span className="truncate">{it.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
    );
  };
  const row = (key: number, label: string, onClick: () => void, onRemove: () => void, icon: React.ReactNode, hint?: string) => (
    <div key={key} className="group flex items-center gap-1 rounded px-2 py-1 hover:bg-sunken">
      <button className="flex min-w-0 flex-1 items-center gap-2 text-left text-value" onClick={onClick} title={hint}>
        {icon}
        <span className="truncate">{label}</span>
      </button>
      <button className="icon-btn h-5 w-5 opacity-0 group-hover:opacity-100" title="Remove" onClick={onRemove}>
        <X size={12} />
      </button>
    </div>
  );
  return (
    <aside className="w-56 shrink-0 overflow-y-auto border-r border-line py-2" aria-label="Filters">
      <div className="mb-1">
        <button className="flex w-full items-center gap-1 px-3 py-1.5 text-[15px] text-ink" onClick={() => setOpen((o) => ({ ...o, Drafts: !(o.Drafts ?? false) }))}>
          {open.Drafts ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
          Drafts {drafts.length > 0 && <span className="ml-1 text-xs text-muted">{drafts.length}</span>}
        </button>
        {open.Drafts && (
          <div className="mb-1 pl-6">
            {drafts.length === 0 && <div className="px-2 py-1 text-muted">No drafts</div>}
            {drafts.map((d) =>
              row(d.id, d.summary || "(untitled)", () => onOpenDraft(d.id), () => void client.http.command("deleteDraft", { draftId: d.id }, uuid()).then(() => client.loadDrafts()), <FileText size={14} className="shrink-0 text-muted" />),
            )}
          </div>
        )}
      </div>
      {section("Projects", (schema?.data.projects ?? []).map((p) => ({ label: p.name, q: `project: ${p.key}` })))}
      {section("Tags", (schema?.data.tags ?? []).map((t) => ({ label: t.name, q: `tag: ${quote(t.name)}`, icon: <Tag size={14} className="text-muted" /> })))}
      <button className="flex w-full items-center gap-2 px-3 py-1.5 text-[15px] text-ink hover:bg-sunken" onClick={() => onQuery("tag: Star")} title="Issues you watch">
        <Star size={16} className="text-star" fill="currentColor" />
        Star
      </button>
      <div className="mb-1">
        <button className="flex w-full items-center gap-1 px-3 py-1.5 text-[15px] text-ink" onClick={() => setOpen((o) => ({ ...o, "Saved Searches": !(o["Saved Searches"] ?? false) }))}>
          {open["Saved Searches"] ? <ChevronDown size={16} className="text-muted" /> : <ChevronRight size={16} className="text-muted" />}
          Saved Searches
        </button>
        {open["Saved Searches"] && (
          <div className="mb-1 pl-6">
            {saved.map((s) => (
              <SavedSearchRow key={s.id} search={s} mine={s.ownerId === me?.id} onQuery={onQuery} onEdit={() => setEditing(s)} />
            ))}
            {currentQuery && !saved.some((s) => s.query === currentQuery) && (
              <button className="px-2 py-1 text-left text-muted hover:text-ink" onClick={() => void saveCurrent()}>+ Save current search…</button>
            )}
            {saved.length === 0 && !currentQuery && <div className="px-2 py-1 text-muted">Run a query, then save it here.</div>}
          </div>
        )}
      </div>
      {editing && <SavedSearchDialog search={editing} onClose={() => setEditing(null)} />}
    </aside>
  );
}

/** One saved search: name, live count on the right (YouTrack's "3k+"), and on hover a ⋯ with Edit / Delete. */
function SavedSearchRow({ search, mine, onQuery, onEdit }: { search: SavedSearch; mine: boolean; onQuery: (q: string) => void; onEdit: () => void }) {
  const sub = `saved:${search.id}`;
  useEffect(() => {
    client.openView(sub, search.query, 0); // count-only subscription
    return () => client.closeView(sub);
  }, [sub, search.query]);
  const total = useClient((s) => s.views[sub]?.total);
  const [menuOpen, setMenuOpen] = useState(false);
  const remove = (): void => {
    if (!confirm(`Delete saved search "${search.name}"?`)) return;
    void client.http.command("deleteSavedSearch", { savedSearchId: search.id }, uuid()).then(() => client.loadSavedSearches());
  };
  return (
    <div className={`group flex items-center gap-1 rounded px-2 py-1 hover:bg-sunken ${menuOpen ? "bg-accent-soft" : ""}`}>
      <button className="flex min-w-0 flex-1 items-center text-left text-value" onClick={() => onQuery(search.query)} title={`${search.query}${mine ? "" : " (shared)"}`}>
        <span className="truncate">{search.name}</span>
      </button>
      {total !== undefined && !menuOpen && <span className="shrink-0 text-xs text-muted tabular-nums group-hover:hidden">{compactCount(total)}</span>}
      <span className={`shrink-0 ${menuOpen ? "inline-flex" : "hidden group-hover:inline-flex"}`}>
        <Menu
          onToggle={setMenuOpen}
          anchor={
            <button className="icon-btn h-6 w-6" title="More">
              <Ellipsis size={16} />
            </button>
          }
          items={[
            { key: "edit", label: "Edit saved search", icon: Pencil, onClick: onEdit },
            { key: "delete", label: "Delete saved search", icon: Trash2, onClick: remove },
          ]}
        />
      </span>
    </div>
  );
}

const quote = (s: string): string => (/[\s:]/.test(s) ? `{${s}}` : s);

